import React, { useEffect } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import clsx from 'clsx'
import { makeStyles } from '@material-ui/styles'
import { Drawer, Paper, Hidden, IconButton } from '@material-ui/core'
import MenuIcon from '@material-ui/icons/Menu'
import { useAuth } from 'hooks/useAuth'
import useRouter from 'utils/useRouter'
import { Navigation } from 'components'
import navigationConfig from './navigationConfig'
import constants from 'utils/constants'
import AddCircleIcon from '@material-ui/icons/AddCircle'

const useStyles = makeStyles((theme) => ({
  root: {
    height: '100%',
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: theme.palette.background.paper,
    // Hide scrollbar for all browsers while keeping functionality
    '&::-webkit-scrollbar': {
      display: 'none',
    },
    '-ms-overflow-style': 'none', // IE and Edge
    'scrollbar-width': 'none', // Firefox
  },
  navHeader: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(1, 2),
    minHeight: 52, // Reduzido de 64 para um visual ultra-compacto sem a logo
  },
  navHeaderExpanded: {
    justifyContent: 'flex-start',
  },
  navHeaderCollapsed: {
    justifyContent: 'center',
  },
  content: {
    flexGrow: 1,
    padding: theme.spacing(1, 2, 2, 2), // Reduzido no topo de 16px para 8px
    opacity: 1,
    transform: 'translateX(0)',
    maxHeight: '600px', // Reduzido para frear o arranque de abertura do navegador
    transition: theme.transitions.create(['opacity', 'transform', 'padding', 'max-height'], {
      easing: theme.transitions.easing.easeInOut,
      duration: 500, // Sincronizado para cadência refinada
    }),
  },
  contentExpanded: {
    padding: theme.spacing(1, 2, 2, 2),
  },
  contentHidden: {
    opacity: 0,
    pointerEvents: 'none',
    transform: 'translateX(-30px)', // Efeito 'slide out' para a esquerda
    maxHeight: 0,
    padding: '0px !important',
    overflow: 'hidden',
  },
  contentCollapsed: {
    padding: theme.spacing(0, 0, 1, 0),
    '& .MuiTypography-overline': {
      display: 'none !important',
    },
    // Forcefully hide sub-elements using global component overrides
    '& span[class*="buttonText"], & div[class*="expandIconContainer"], & span[class*="label"]': {
      display: 'none !important',
    },
    // Recenter button items
    '& button[class*="button"]': {
      justifyContent: 'center !important',
      padding: '12px 0 !important',
    },
    // Wipe icon margins
    '& span[class*="icon"]': {
      marginRight: '0 !important',
    }
  },
  navigation: {
    marginTop: theme.spacing(2),
  },
  quickActionContainer: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(1, 0, 2, 0),
    opacity: 1,
    transform: 'scale(1) translateY(0)',
    maxHeight: '100px',
    transition: theme.transitions.create(['opacity', 'transform', 'max-height', 'padding'], {
      easing: theme.transitions.easing.easeInOut,
      duration: 500, // Sincronizado para 500ms
    }),
    overflow: 'hidden',
  },
  quickActionContainerHidden: {
    opacity: 0,
    pointerEvents: 'none',
    transform: 'scale(0.6) translateY(-20px)',
    maxHeight: 0,
    padding: '0px !important',
  },
  quickActionButton: {
    color: theme.palette.text.primary,
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: theme.palette.type === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
      transform: 'scale(1.1)',
    }
  },
}))

export const NavBar = (props) => {
  const { openMobile, onMobileClose, isExpanded = true, onToggleExpand, className, ...rest } = props
  const { verificarPermissao, verificarSetor } = useAuth()
  const classes = useStyles()
  const router = useRouter()

  useEffect(() => {
    if (openMobile && onMobileClose) {
      onMobileClose()
    }
  }, [router.location.pathname])

  const renderNavbarContent = (activeExpanded, showToggle = false) => (
    <>
      {/* Cabeçalho: Renderiza APENAS no Desktop (onde showToggle é true), economizando espaço vital no Mobile */}
      {showToggle && (
        <div className={clsx(classes.navHeader, activeExpanded ? classes.navHeaderExpanded : classes.navHeaderCollapsed)}>
          <IconButton onClick={onToggleExpand} size="medium">
            <MenuIcon />
          </IconButton>
        </div>
      )}
      
      {/* MODO MINIMIZADO: Apenas ícone de Ação Rápida - Animação por CSS */}
      {verificarPermissao([constants.PERMISSOES.CADASTRAR_CASO]) && (
        <div className={clsx(classes.quickActionContainer, activeExpanded && classes.quickActionContainerHidden)}>
          <IconButton 
            component={RouterLink} 
            to="/casos/cadastrar" 
            className={classes.quickActionButton}
            title="Cadastrar Caso"
          >
            <AddCircleIcon style={{ fontSize: 24 }} />
          </IconButton>
        </div>
      )}

      {/* MODO EXPANDIDO: Árvore de Navegação Completa - Slide e Fade suave */}
      <div className={clsx(classes.content, !activeExpanded && classes.contentHidden)}>
        <nav className={classes.navigation}>
          {navigationConfig.map((list) => {
            const paginasPermitidas = []
            list.pages.forEach((page) => {
              if(page.href == '/centraldecustodia'){
                if(verificarSetor([184])){
                  paginasPermitidas.push(page)
                }
              }else{
                if (verificarPermissao([page.permissao])) {
                  paginasPermitidas.push(page)
                }
              }
              
            })
            return <Navigation component="div" key={list.title} pages={paginasPermitidas} title={list.title} isExpanded={activeExpanded} />
          })}
        </nav>
      </div>
    </>
  )

  return (
    <>
      <Hidden lgUp>
        <Drawer anchor="left" onClose={onMobileClose} open={openMobile} variant="temporary">
          <div {...rest} className={clsx(classes.root, className)}>
            {renderNavbarContent(true, false)} {/* Mobile: SEMPRE expandido, SEM botão interno */}
          </div>
        </Drawer>
      </Hidden>
      <Hidden mdDown>
        <Paper {...rest} className={clsx(classes.root, className)} elevation={1} square>
          {renderNavbarContent(isExpanded, true)} {/* Desktop: Estado nativo com botão interno */}
        </Paper>
      </Hidden>
    </>
  )
}
